import { useEffect, useId, useRef, useState } from 'react'
import type { RoundMediaItem } from '../../lib/roundMedia'
import {
  roundMediaContentPath,
  roundMediaHoleCounts,
  roundMediaPosterPath,
} from '../../lib/roundMedia'
import { formatPlaybackTime } from '../../lib/roundMediaPlayback'
import { AreaSectionHead } from './PageHead'
import { RoundMediaVideoPlayer } from './RoundMediaVideoPlayer'
import type { RoundMediaVideoPlayerHandle } from './RoundMediaVideoPlayer'

export interface RoundMediaSectionProps {
  media: readonly RoundMediaItem[]
  isLoading: boolean
  error?: string | null
  /** A shared-hole link opens the gallery on that hole, even when it is empty. */
  initialHole?: number | null
  /** Present only on the signed-in member surface. */
  onRemove?: (item: RoundMediaItem) => Promise<void>
}

interface Selection {
  initialHole: number | null
  selectedHole: number | null
}

interface OpenItem {
  item: RoundMediaItem
  trigger: HTMLButtonElement
}

/** Ready round media shared by the member and public round books. */
export function RoundMediaSection({
  media,
  isLoading,
  error = null,
  initialHole = null,
  onRemove,
}: RoundMediaSectionProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const [selection, setSelection] = useState<Selection>({ initialHole, selectedHole: initialHole })
  const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(new Set())
  const [removalAnnouncement, setRemovalAnnouncement] = useState('')
  const [removalError, setRemovalError] = useState<string | null>(null)
  const [pendingRemovalId, setPendingRemovalId] = useState<string | null>(null)
  const [viewer, setViewer] = useState<OpenItem | null>(null)
  const [removal, setRemoval] = useState<OpenItem | null>(null)
  const selectedHole = selection.initialHole === initialHole ? selection.selectedHole : initialHole
  const ready = media.filter(
    (item) => item.state === 'ready' && item.kind !== null && !removedIds.has(item.id),
  )
  const counts = roundMediaHoleCounts(ready)
  const shown = selectedHole === null
    ? ready
    : ready.filter((item) => item.hole_number === selectedHole)

  const chooseHole = (hole: number | null) => {
    setSelection({ initialHole, selectedHole: hole })
  }

  const remove = async (item: RoundMediaItem) => {
    if (!onRemove) return
    const kind = item.kind === 'video' ? 'Video' : 'Photo'
    const description = mediaDescription(item)
    setRemovalError(null)
    setPendingRemovalId(item.id)
    setRemovalAnnouncement(`Removing ${kind.toLowerCase()} from round media…`)
    try {
      await onRemove(item)
      setRemovedIds((current) => new Set(current).add(item.id))
      setRemovalAnnouncement(
        `${sentenceCase(description)} removed from round media.`,
      )
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Could not remove this media.'
      setRemovalError(message)
      setRemovalAnnouncement(message)
    } finally {
      setPendingRemovalId(null)
    }
  }

  useEffect(() => {
    if (removalAnnouncement) sectionRef.current?.focus()
  }, [removedIds, removalAnnouncement])

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-label="Round media"
      data-round-section="media"
      data-round-media=""
    >
      <AreaSectionHead eyebrow="Media" title="The round, caught." />

      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {removalAnnouncement}
      </p>

      {removalError && (
        <p className="mb-3 max-w-[620px] rounded-club border border-flag-deep px-4 py-3 text-[13px] text-flag-deep" role="alert">
          {removalError}
        </p>
      )}

      {isLoading && (
        <p className="text-[13px] text-ink-soft" role="status">
          Opening the round’s media…
        </p>
      )}

      {!isLoading && error && (
        <p className="rounded-club border border-brass bg-parchment px-4 py-3 text-[13px] text-ink" role="alert">
          {error}
        </p>
      )}

      {!isLoading && !error && ready.length === 0 && (
        <div className="max-w-[620px] rounded-club-lg border border-dashed border-brass bg-parchment px-5 py-5">
          <h3 className="font-display text-[21px] font-medium text-pine">No media from this round.</h3>
          <p className="mt-1 text-[13px] text-ink-soft">
            Ready photos and videos will appear here when they are available.
          </p>
        </div>
      )}

      {!isLoading && !error && ready.length > 0 && (
        <div className="max-w-[860px]">
          <p className="mb-3 max-w-[65ch] text-[13px] text-ink-soft">
            Ready photos and videos in round order. Choose a hole without changing the list.
          </p>

          <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Filter round media by hole">
            <FilterButton
              active={selectedHole === null}
              count={ready.length}
              label="All"
              onClick={() => chooseHole(null)}
            />
            {[...counts.entries()].map(([hole, count]) => (
              <FilterButton
                key={hole}
                active={selectedHole === hole}
                count={count}
                label={`Hole ${hole}`}
                onClick={() => chooseHole(hole)}
              />
            ))}
          </div>

          {shown.length === 0 ? (
            <p
              className="rounded-club border border-dashed border-brass px-4 py-3 text-[12.5px] text-ink-soft"
              role="status"
            >
              No ready media on hole {selectedHole}. The round has ready media on other holes.
            </p>
          ) : (
            <ol className="flex flex-col gap-4" aria-label="Round media">
              {shown.map((item) => (
                <MediaCard
                  key={item.id}
                  item={item}
                  removalDisabled={pendingRemovalId !== null}
                  removalPending={pendingRemovalId === item.id}
                  onOpen={(trigger) => setViewer({ item, trigger })}
                  onRequestRemoval={
                    onRemove && item.removable
                      ? (trigger) => setRemoval({ item, trigger })
                      : undefined
                  }
                />
              ))}
            </ol>
          )}
        </div>
      )}

      {viewer && (
        <MediaViewer
          key={viewer.item.id}
          item={viewer.item}
          returnFocus={viewer.trigger}
          onClose={() => setViewer(null)}
        />
      )}
      {removal && onRemove && (
        <RemovalDialog
          key={removal.item.id}
          item={removal.item}
          returnFocus={removal.trigger}
          onCancel={() => setRemoval(null)}
          onConfirm={() => {
            const item = removal.item
            setRemoval(null)
            void remove(item)
          }}
        />
      )}
    </section>
  )
}

function FilterButton({
  active,
  count,
  label,
  onClick,
}: {
  active: boolean
  count: number
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={`${label}, ${count} ${count === 1 ? 'media item' : 'media items'}`}
      onClick={onClick}
      className={
        active
          ? 'min-h-11 rounded-full border border-pine bg-pine px-4 text-[12px] font-semibold text-paper'
          : 'min-h-11 rounded-full border border-brass-deep bg-parchment px-4 text-[12px] font-semibold text-ink'
      }
    >
      {label} <span className={active ? 'ml-1 tabular-nums text-sage' : 'ml-1 tabular-nums text-brass-deep'}>{count}</span>
    </button>
  )
}

function MediaCard({
  item,
  removalDisabled,
  removalPending,
  onOpen,
  onRequestRemoval,
}: {
  item: RoundMediaItem
  removalDisabled: boolean
  removalPending: boolean
  onOpen: (trigger: HTMLButtonElement) => void
  onRequestRemoval?: (trigger: HTMLButtonElement) => void
}) {
  const contentPath = roundMediaContentPath(item)
  const posterPath = roundMediaPosterPath(item)
  const previewPath = item.kind === 'photo' ? contentPath : posterPath
  const kind = item.kind === 'video' ? 'Video' : 'Photo'
  const description = mediaDescription(item)
  const subjectLabel = mediaSubjectLabel(item)
  const openable = contentPath !== null

  return (
    <li className="grid overflow-hidden rounded-club-lg border border-sage-soft bg-parchment md:grid-cols-[minmax(250px,1.25fr)_minmax(210px,.75fr)]">
      <button
        type="button"
        disabled={!openable}
        onClick={(event) => onOpen(event.currentTarget)}
        aria-haspopup="dialog"
        aria-label={`Open ${description}`}
        className="group relative min-h-[210px] overflow-hidden bg-fairway text-left text-paper disabled:cursor-not-allowed"
      >
        {previewPath ? (
          <img
            src={previewPath}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <span className="absolute inset-0 grid place-items-center bg-pine/65 text-[12px] text-paper">
            Preview unavailable
          </span>
        )}
        <span className="absolute left-3 top-3 rounded-club-sm bg-pine/90 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.14em]">
          {kind}{item.kind === 'video' ? ' · muted' : ''}
        </span>
        {item.kind === 'video' && (
          <>
            <span className="absolute inset-0 grid place-items-center text-[28px]" aria-hidden="true">▶</span>
            {item.duration_seconds !== null && (
              <span className="absolute bottom-3 right-3 rounded-club-sm bg-pine/90 px-2 py-1 text-[10px] tabular-nums">
                {formatPlaybackTime(item.duration_seconds)}
              </span>
            )}
          </>
        )}
        {/* The card clips its artwork, including the shared outside outline.
            Keep a complete two-tone focus perimeter inside that clipping. */}
        <span
          aria-hidden="true"
          data-media-focus-ring=""
          className="pointer-events-none absolute inset-1 z-20 hidden rounded-club-sm border-[3px] border-paper outline-[3px] outline-fairway group-focus-visible:block"
        />
      </button>

      <div className="flex min-w-0 flex-col p-5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-brass-deep">
          Hole {item.hole_number} · {kind}
        </span>
        <h3 className="mt-1 font-display text-[22px] font-medium text-pine">
          {subjectLabel}
        </h3>
        <p className="mt-1 text-[12.5px] text-ink-soft">
          Uploaded by {item.uploader_display_name}.
        </p>
        {item.shot && (
          <p className="mt-3 border-t border-sage-soft pt-2 text-[11.5px] font-semibold text-fairway">
            {shotLabel(item.shot)}
          </p>
        )}
        {onRequestRemoval && (
          <button
            type="button"
            disabled={removalDisabled}
            onClick={(event) => onRequestRemoval(event.currentTarget)}
            aria-haspopup="dialog"
            className="mt-auto min-h-11 self-start pt-3 text-[11.5px] font-semibold text-ink-soft underline underline-offset-4 disabled:no-underline disabled:opacity-60"
          >
            {removalPending ? 'Removing media…' : 'Remove media'}
            <span className="sr-only">
              {`: ${description}`}
            </span>
          </button>
        )}
      </div>
    </li>
  )
}

function MediaViewer({
  item,
  returnFocus,
  onClose,
}: {
  item: RoundMediaItem
  returnFocus: HTMLButtonElement
  onClose: () => void
}) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const playerRef = useRef<RoundMediaVideoPlayerHandle>(null)
  // Set once the viewer has closed by either path below, so the platform's
  // `close` event that follows our own `dialog.close()` — or the unmount —
  // never stops the video and returns focus a second time.
  const closedRef = useRef(false)
  const [fullscreen, setFullscreen] = useState(false)
  const contentPath = roundMediaContentPath(item)
  const subjectLabel = mediaSubjectLabel(item)
  const description = sentenceCase(mediaDescription(item))

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    closedRef.current = false
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    }
    closeRef.current?.focus()
    return () => {
      closedRef.current = true
      if (typeof dialog.close === 'function' && dialog.open) dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])

  const finish = () => {
    playerRef.current?.pause()
    onClose()
    returnFocus.focus()
  }

  const close = () => {
    if (closedRef.current) return
    closedRef.current = true
    const dialog = dialogRef.current
    if (typeof dialog?.close === 'function' && dialog.open) dialog.close()
    else dialog?.removeAttribute('open')
    finish()
  }

  // The platform closed the dialog itself: Chrome's close watcher may refuse
  // a second Escape's preventDefault without fresh user activation, and the
  // dialog is already shut by the time the `close` event says so. The video
  // still stops and focus still returns to the card that opened it.
  //
  // A `close` that finds the dialog OPEN is stale, not the platform's:
  // `dialog.close()` only queues the event, and StrictMode's mount → unmount
  // → remount in development lands the cleanup's close on the dialog that
  // `showModal()` has just re-opened. Taken at its word, the viewer closed
  // itself the moment it opened.
  const platformClosed = () => {
    if (closedRef.current || dialogRef.current?.open) return
    closedRef.current = true
    finish()
  }

  return (
    <dialog
      ref={dialogRef}
      className="on-pine fixed inset-0 z-50 m-0 hidden h-full max-h-none w-full max-w-none place-items-center overflow-y-auto border-0 bg-ink/70 p-4 open:grid"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Escape leaves full screen first and closes the viewer second.
        event.preventDefault()
        if (fullscreen) setFullscreen(false)
        else close()
      }}
      onClose={platformClosed}
      onKeyDown={(event) => playerRef.current?.shortcut(event)}
    >
      <div className="w-full max-w-[760px] overflow-hidden rounded-club-lg border border-gilt bg-pine text-paper shadow-2xl">
        {/* Under the full-screen layer the rest of the dialog is beneath it:
            unreachable and unread, so the layer's focus scope is the player's
            own controls rather than the whole dialog's (WCAG 2.4.11). */}
        <div
          inert={fullscreen || undefined}
          aria-hidden={fullscreen || undefined}
          className="flex items-center justify-between gap-4 border-b border-sage-soft/25 px-4 py-3"
        >
          <div>
            <h2 id={titleId} className="font-display text-[20px] font-medium">
              Hole {item.hole_number} · {subjectLabel}
            </h2>
            <p className="text-[11.5px] text-sage">Uploaded by {item.uploader_display_name}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            className="min-h-11 min-w-11 rounded-full border border-gilt text-[22px]"
            aria-label="Close media viewer"
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-5">
          <div className="grid min-h-[280px] place-items-center overflow-hidden rounded-club bg-ink">
            {item.kind === 'photo' && contentPath && (
              <img src={contentPath} alt={description} className="max-h-[70vh] w-full object-contain" />
            )}
            {item.kind === 'video' && contentPath && (
              // makaira-jo8v.18 owns captions/audio alternatives before GA.
              <RoundMediaVideoPlayer
                ref={playerRef}
                item={item}
                description={description}
                fullscreen={fullscreen}
                onFullscreenChange={setFullscreen}
              />
            )}
          </div>

          {item.shot && (
            <p
              inert={fullscreen || undefined}
              aria-hidden={fullscreen || undefined}
              className="mt-3 text-[12px] font-semibold text-sage"
            >
              {shotLabel(item.shot)}
            </p>
          )}
        </div>
      </div>
    </dialog>
  )
}

function RemovalDialog({
  item,
  returnFocus,
  onCancel,
  onConfirm,
}: {
  item: RoundMediaItem
  returnFocus: HTMLButtonElement
  onCancel: () => void
  onConfirm: () => void
}) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const subjectLabel = mediaSubjectLabel(item)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    }
    cancelRef.current?.focus()
    return () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])

  const cancel = () => {
    const dialog = dialogRef.current
    if (typeof dialog?.close === 'function' && dialog.open) dialog.close()
    else dialog?.removeAttribute('open')
    onCancel()
    returnFocus.focus()
  }

  const confirm = () => {
    const dialog = dialogRef.current
    if (typeof dialog?.close === 'function' && dialog.open) dialog.close()
    else dialog?.removeAttribute('open')
    onConfirm()
  }

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-0 hidden h-full max-h-none w-full max-w-none place-items-center overflow-y-auto border-0 bg-ink/70 p-4 open:grid"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault()
        cancel()
      }}
    >
      <div className="w-full max-w-[430px] rounded-club-lg border border-brass bg-parchment p-5 text-ink shadow-2xl">
        <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-brass-deep">
          Privacy control
        </span>
        <h2 id={titleId} className="mt-1 font-display text-[24px] font-medium text-pine">
          Remove this media?
        </h2>
        <p id={descriptionId} className="mt-2 text-[13px] leading-relaxed text-ink-soft">
          {subjectLabel} · Hole {item.hole_number} will disappear from the member and shared round immediately. This cannot be undone.
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={cancel}
            className="min-h-11 rounded-club border border-pine px-4 text-[12px] font-semibold text-pine"
          >
            Keep media
          </button>
          <button
            type="button"
            onClick={confirm}
            className="min-h-11 rounded-club border border-pine bg-pine px-4 text-[12px] font-semibold text-paper"
          >
            Remove media
          </button>
        </div>
      </div>
    </dialog>
  )
}

function mediaDescription(item: RoundMediaItem): string {
  const kind = item.kind === 'video' ? 'video' : 'photo'
  return item.subject.kind === 'round_moment'
    ? `Round Moment ${kind} on hole ${item.hole_number}`
    : `${kind} for ${mediaSubjectLabel(item)} on hole ${item.hole_number}`
}

function mediaSubjectLabel(item: RoundMediaItem): string {
  return item.subject.kind === 'round_moment'
    ? 'Round Moment'
    : item.subject.display_name
}

function sentenceCase(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function shotLabel(shot: NonNullable<RoundMediaItem['shot']>): string {
  return [shot.club, `Shot ${shot.sequence}`].filter(Boolean).join(' · ')
}
